// ============================================================
// AluminumEstimator.js
// Single-screen Aluminum estimator.
//
// Mirrors VinylEstimator.js exactly: same navigation contract (returnTo /
// previousSpecs / priceLevel via location.state), same flat one-page card
// layout, same design tokens, and the same hand-off — on "Calculate & Add to
// Document" it runs calculateAluminum and navigates back to the originating
// document with { lineItems, estimatorSpecs, priceLevel }, the shape
// NewDocument/EditDocument already ingest generically.
//
// specs.kind = 'aluminum' is the discriminator, mirroring vinyl's 'vinyl'.
//
// Product is FIXED — residential aluminum, 54" high, 6'-wide panels — so, like
// vinyl, there are no style or height selectors; the product is stated in the
// header. Differences from the vinyl page: gates carry a `shape` (flat/arched),
// there is no post-cap control (aluminum has none), and no stiffener.
// ============================================================

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { calculateAluminum } from '../utils/aluminumCalc';

let runIdCounter = 1;
const newRun = () => ({ id: runIdCounter++, length: '' });

export default function AluminumEstimator({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { products } = useData();
  const returnTo = location?.state?.returnTo || null;
  const previousSpecs = location?.state?.previousSpecs || null;
  const docPriceLevel = location?.state?.priceLevel || 'A';
  const userName = user?.email?.split('@')[0]?.toUpperCase();

  // ── State (seeded from previousSpecs so "modify" pre-fills) ──
  const [runs, setRuns] = useState(() => {
    if (previousSpecs?.runs?.length > 0) {
      // Reassign IDs to avoid collisions with the module-level counter.
      return previousSpecs.runs.map(r => ({ id: runIdCounter++, length: r.length ?? '' }));
    }
    return [newRun()];
  });
  const [totalCorners, setTotalCorners] = useState(() => previousSpecs?.totalCorners ?? 0);
  const [totalEnds, setTotalEnds] = useState(() => previousSpecs?.totalEnds ?? 0);
  const [latchType, setLatchType] = useState(() => previousSpecs?.latchType || 'standard');
  const [gates, setGates] = useState(() => previousSpecs?.gates || []);
  const [result, setResult] = useState(null); // { items, warnings } preview

  const runInputRefs = useRef({});

  // ── Run helpers (copied from VinylEstimator) ──
  const updateRun = (id, length) => setRuns(prev => prev.map(r => r.id === id ? { ...r, length } : r));
  const removeRun = (id) => { if (runs.length > 1) setRuns(prev => prev.filter(r => r.id !== id)); };
  const addRun = () => {
    const nr = newRun();
    setRuns(prev => [...prev, nr]);
    setTimeout(() => runInputRefs.current[nr.id]?.focus(), 50);
  };
  const handleRunKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); addRun(); } };

  // ── Gate helpers (simple inline list — no modal). Gates carry a `shape`,
  // which vinyl's do not; everything else matches vinyl's gate object. ──
  const addGate = () => setGates(prev => [...prev, { width: '4', shape: 'flat', kind: 'single' }]);
  const updateGate = (idx, field, value) => setGates(prev => prev.map((g, i) => i === idx ? { ...g, [field]: value } : g));
  const removeGate = (idx) => setGates(prev => prev.filter((_, i) => i !== idx));

  // Any spec change invalidates a previewed result.
  useEffect(() => { setResult(null); }, [runs, totalCorners, totalEnds, latchType, gates]);

  // ── Specs — field names match calculateAluminum's contract exactly ──
  const buildSpecs = () => ({
    kind: 'aluminum', // discriminator so the correct "Modify" route can be chosen later
    runs,
    totalCorners: parseInt(totalCorners) || 0,
    totalEnds: parseInt(totalEnds) || 0,
    latchType,
    gates,
  });

  // ── Derived ──
  const totalFootage = runs.reduce((s, r) => s + (parseFloat(r.length) || 0), 0);
  // Vinyl requires footage; aluminum also allows a gates-only job (a gate can be
  // quoted on its own), so either input is enough to calculate.
  const canCalc = runs.some(r => parseFloat(r.length) > 0) || gates.length > 0;

  // ── Calculate ──
  const handlePreview = () => {
    if (!products || products.length === 0) {
      alert('Products are still loading. Please wait a moment and try again.');
      return;
    }
    setResult(calculateAluminum(buildSpecs(), products, docPriceLevel));
  };

  // Mirrors VinylEstimator.handleCalculate exactly.
  const handleCalculate = () => {
    if (returnTo && (!products || products.length === 0)) {
      alert('Products are still loading. Please wait a moment and try again.');
      return;
    }
    const specs = buildSpecs();
    if (returnTo) {
      const { items } = calculateAluminum(specs, products, docPriceLevel);
      navigate(returnTo, { state: { lineItems: items, estimatorSpecs: specs, priceLevel: docPriceLevel } });
    } else {
      // No standalone results page for aluminum — preview in place, then dashboard.
      navigate('/dashboard');
    }
  };

  // ── Shared inline styles (identical tokens to VinylEstimator) ──
  const cardStyle = { background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16 };
  const sectionTitle = { color: '#03045E', fontWeight: 700, margin: '0 0 12px 0' };
  const labelStyle = { color: '#6B7C93', fontSize: 12, fontWeight: 600, marginBottom: 6, display: 'block' };
  const inputStyle = {
    width: '100%', border: '1px solid #E3ECF5', borderRadius: 10, padding: '8px 12px',
    fontSize: 16, fontWeight: 700, color: '#03045E', background: '#FFFFFF', outline: 'none',
    boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s',
  };
  const selectStyle = { ...inputStyle, fontSize: 14 };
  const focusOn = (e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; };
  const focusOff = (e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; };
  const noteStyle = { color: '#6B7C93', fontSize: 12, margin: '6px 0 0 0' };

  return (
    <div className="min-h-screen" style={{ background: '#CDE7F5', padding: 20 }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>

        {/* 1. Header — the product is fixed, so it is stated here rather than picked. */}
        <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, background: '#E6F1FB', border: '1px solid #E3ECF5', borderRadius: 12, flexShrink: 0 }}>
              <img src={require('../assets/fenceflow-logo.png')} alt="Fence Flow" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8 }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: '#03045E' }}>Aluminum Estimator</h1>
              <p style={{ color: '#6B7C93', fontSize: 13, margin: '2px 0 0 0' }}>Residential Aluminum · 54&quot; · 6' panels{userName ? ` · ${userName}` : ''}</p>
            </div>
          </div>
        </div>

        {/* 2. Fence Layout */}
        <div style={cardStyle}>
          <p style={sectionTitle}>Fence Layout</p>

          <div className="space-y-2" style={{ marginBottom: 8 }}>
            {runs.map((run, idx) => (
              <div key={run.id} className="flex items-center gap-2">
                <span style={{ color: '#6B7C93', fontSize: 12, width: 56, flexShrink: 0 }}>Run {idx + 1}</span>
                <input
                  type="number"
                  value={run.length === 0 ? '' : run.length}
                  ref={el => { runInputRefs.current[run.id] = el; }}
                  onChange={e => updateRun(run.id, e.target.value)}
                  onKeyDown={handleRunKeyDown}
                  onFocus={e => { e.target.select(); focusOn(e); }}
                  onBlur={focusOff}
                  style={{ ...inputStyle, flex: 1, fontSize: 18 }}
                  placeholder="ft"
                  min="1"
                />
                <span style={{ color: '#6B7C93', fontSize: 12 }}>ft</span>
                {runs.length > 1 && (
                  <button onClick={() => removeRun(run.id)} style={{ background: 'none', border: 'none', color: '#A32D2D', fontWeight: 700, padding: '0 4px', fontSize: 18, lineHeight: 1, cursor: 'pointer' }}>×</button>
                )}
              </div>
            ))}
          </div>
          <button onClick={addRun} style={{ background: 'none', border: 'none', color: '#0077B6', fontSize: 14, fontWeight: 700, padding: '4px 0', cursor: 'pointer' }}>
            + Add Run
          </button>
          {totalFootage > 0 && (
            <div style={{ marginTop: 12, background: '#E6F1FB', border: '1px solid #E3ECF5', borderRadius: 10, padding: 12 }}>
              <p style={{ color: '#03045E', fontWeight: 700, margin: 0 }}>Total: {totalFootage.toLocaleString()} ft</p>
              <p style={{ color: '#0077B6', fontSize: 12, margin: '2px 0 0 0' }}>{runs.filter(r => parseFloat(r.length) > 0).length} run(s)</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3" style={{ marginTop: 16 }}>
            <div>
              <label style={labelStyle}>Corner Posts</label>
              <input
                type="number" min="0"
                value={totalCorners === 0 ? '' : totalCorners}
                onChange={e => setTotalCorners(e.target.value)}
                onFocus={e => { e.target.select(); focusOn(e); }} onBlur={focusOff}
                style={inputStyle} placeholder="0"
              />
            </div>
            <div>
              <label style={labelStyle}>End Posts</label>
              <input
                type="number" min="0"
                value={totalEnds === 0 ? '' : totalEnds}
                onChange={e => setTotalEnds(e.target.value)}
                onFocus={e => { e.target.select(); focusOn(e); }} onBlur={focusOff}
                style={inputStyle} placeholder="0"
              />
            </div>
          </div>
          {/* Aluminum has a dedicated gate post, so gate posts are NOT counted here
              (the engine derives them). The closing side of a single gate is still
              an end post the user has to count. */}
          <p style={noteStyle}>Include the closing-side end post of any single gates here. Gate posts are counted automatically.</p>
        </div>

        {/* 3. Gates */}
        <div style={cardStyle}>
          <p style={sectionTitle}>Gates</p>
          {gates.length === 0 ? (
            <p style={{ color: '#6B7C93', fontSize: 13, margin: '0 0 8px 0' }}>No gates</p>
          ) : (
            <div className="space-y-2" style={{ marginBottom: 8 }}>
              {gates.map((gate, i) => (
                <div key={i} className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                  <span style={{ color: '#6B7C93', fontSize: 12, width: 56, flexShrink: 0 }}>Gate {i + 1}</span>
                  <select value={gate.width} onChange={e => updateGate(i, 'width', e.target.value)} onFocus={focusOn} onBlur={focusOff} style={{ ...selectStyle, flex: 1, minWidth: 80 }}>
                    <option value="4">4'</option>
                    <option value="5">5'</option>
                  </select>
                  <select value={gate.shape} onChange={e => updateGate(i, 'shape', e.target.value)} onFocus={focusOn} onBlur={focusOff} style={{ ...selectStyle, flex: 1, minWidth: 90 }}>
                    <option value="flat">Flat top</option>
                    <option value="arched">Arched top</option>
                  </select>
                  <select value={gate.kind} onChange={e => updateGate(i, 'kind', e.target.value)} onFocus={focusOn} onBlur={focusOff} style={{ ...selectStyle, flex: 1, minWidth: 100 }}>
                    <option value="single">Single</option>
                    <option value="double">Double Pair</option>
                  </select>
                  <button onClick={() => removeGate(i)} style={{ background: 'none', border: 'none', color: '#A32D2D', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>Remove</button>
                </div>
              ))}
            </div>
          )}
          <button onClick={addGate} style={{ background: 'none', border: 'none', color: '#0077B6', fontSize: 14, fontWeight: 700, padding: '4px 0', cursor: 'pointer' }}>
            + Add Gate
          </button>
          <p style={noteStyle}>Double = both leaves entered as one gate. Single gate uses 1 gate post; double uses 2.</p>
        </div>

        {/* 4. Options — latch only; aluminum has no post caps. */}
        <div style={cardStyle}>
          <p style={sectionTitle}>Options</p>
          <div>
            <label style={labelStyle}>Latch</label>
            <select value={latchType} onChange={e => setLatchType(e.target.value)} onFocus={focusOn} onBlur={focusOff} style={selectStyle}>
              <option value="standard">Standard Gravity Latch</option>
              <option value="topPull">Top-pull latch (pool code)</option>
            </select>
            <p style={noteStyle}>Applies to every gate on this job. One latch per gate, single or double.</p>
          </div>
        </div>

        {/* 5. Materials preview */}
        {result && (
          <div style={cardStyle}>
            <p style={sectionTitle}>Materials ({result.items.length} lines)</p>
            {result.warnings.length > 0 && (
              <div style={{ background: '#FBECEC', border: '1px solid #E9C3C3', borderRadius: 10, padding: 12, marginBottom: 12 }}>
                <p style={{ color: '#A32D2D', fontSize: 13, fontWeight: 700, margin: 0 }}>
                  {result.warnings.length} SKU(s) not found in the price list:
                </p>
                <p style={{ color: '#A32D2D', fontSize: 12, margin: '4px 0 0 0' }}>{result.warnings.map(w => String(w)).join(', ')}</p>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {result.items.map(item => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'baseline', gap: 10, fontSize: 13 }}>
                  <span style={{ color: '#0077B6', fontFamily: 'monospace', fontWeight: 700, width: 100, flexShrink: 0 }}>{String(item.sku)}</span>
                  <span style={{ color: item.found ? '#03045E' : '#A32D2D', flex: 1, minWidth: 0 }}>{item.description}</span>
                  <span style={{ color: '#03045E', fontWeight: 700, flexShrink: 0 }}>{item.qty} {item.unit}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 6. Footer */}
        <div className="flex justify-between" style={{ marginTop: 4, gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate(returnTo || '/dashboard')}
            style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E', fontWeight: 700, padding: '12px 28px', borderRadius: 12, fontSize: 15, cursor: 'pointer' }}>
            Cancel
          </button>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              onClick={handlePreview}
              disabled={!canCalc}
              style={{
                background: '#FFFFFF', border: `1px solid ${canCalc ? '#0077B6' : '#E3ECF5'}`,
                color: canCalc ? '#0077B6' : '#6B7C93',
                fontWeight: 700, padding: '12px 24px', borderRadius: 12, fontSize: 15,
                cursor: canCalc ? 'pointer' : 'default',
              }}>
              Preview Materials
            </button>
            <button
              onClick={handleCalculate}
              disabled={!canCalc}
              style={{
                background: canCalc ? '#0077B6' : '#E3ECF5',
                border: 'none', color: canCalc ? '#FFFFFF' : '#6B7C93',
                fontWeight: 700, padding: '12px 28px', borderRadius: 12, fontSize: 15,
                cursor: canCalc ? 'pointer' : 'default',
                boxShadow: canCalc ? '0 4px 14px rgba(0,119,182,0.3)' : 'none',
              }}>
              Calculate &amp; Add to Document
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
